import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import ModalConfirmLogout from "../../components/ModalConfirmLogout";
import SocialMediaIcons from "../../components/SocialMediaIcons";
import { ApiService, VideoData } from "../../services/apiService";

const TongHopVideo: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<string | number | null>(null);
  const [videos, setVideos] = useState<VideoData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load videos from API only
  useEffect(() => {
    let isMounted = true;
    
    const loadVideos = async () => {
      try {
        setLoading(true);
        const videosData = await ApiService.getAllVideos();
        if (isMounted) {
          setVideos(videosData);
        }
      } catch (error) {
        if (isMounted) {
          setVideos([]); // Empty array if API fails
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadVideos();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const handleVideoClick = (videoId: string | number) => {
    setSelectedVideo(videoId);
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    // Xử lý khi user chọn "Có" - chuyển đến trang video player
    if (selectedVideo) {
      try {
        // Increment view count when user confirms to watch video
        await ApiService.incrementVideoView(selectedVideo);
      } catch (error) {
        // Silent fail for view count increment
      }
      
      navigate(`/video/${selectedVideo}`);
    }
    setShowConfirmModal(false);
    setSelectedVideo(null);
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
    setSelectedVideo(null);
  };

  return (
    <div className="min-h-screen relative">
      <Header setIsShowLogout={() => setShowLogoutModal(true)} />
      
      {/* Background Image */}
      <div 
        className="fixed inset-0 w-full h-full -z-10"
        style={{
          backgroundImage: `url(${isMobile ? "/assets/bg_mobile_2.jpg" : "/assets/bg_pc_2.jpg"})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      ></div>

      {/* Logo and Back Button - Same as 50 Cong Thuc */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          {/* Logo - positioned above KT01 (first image) - Hidden on mobile */}
          <img 
            src="/assets/logo-2.png" 
            alt="logo" 
            className="h-20 hidden sm:block"
          />
          
          {/* Back Button - positioned above KT04 (fourth image) - Smaller on mobile */}
          <button
            onClick={() => navigate("/")}
            className="group relative flex items-center justify-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 rounded-full font-bold text-[#DF3125] transition-all duration-500 hover:scale-110 hover:rotate-1 overflow-hidden
                       text-sm sm:text-lg"
            style={{
              background: 'linear-gradient(135deg, #F9D332 0%, #FFD700 50%, #F9D332 100%)',
              border: '3px solid #FFA500',
              boxShadow: '0 8px 25px rgba(249, 211, 50, 0.4), inset 0 2px 0 rgba(255, 255, 255, 0.3), inset 0 -2px 0 rgba(0, 0, 0, 0.1)',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              animation: 'pulse-glow 2s ease-in-out infinite alternate'
            }}
          >
            {/* Animated background shimmer */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            
            {/* Left arrows with animation */}
            <div className="flex group-hover:animate-bounce">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 group-hover:text-red-600 transition-colors duration-300" fill="currentColor" viewBox="0 0 24 24">
                <path d="M15 19l-7-7 7-7" stroke="currentColor" strokeWidth="3" fill="none"/>
              </svg>
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 group-hover:text-red-600 transition-colors duration-300 -ml-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M15 19l-7-7 7-7" stroke="currentColor" strokeWidth="3" fill="none"/>
              </svg>
            </div>
            
            <span className="font-extrabold group-hover:tracking-wider transition-all duration-300">Quay lại</span>
            
            {/* Right arrows with animation */}
            <div className="flex group-hover:animate-bounce">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 group-hover:text-red-600 transition-colors duration-300" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="3" fill="none"/>
              </svg>
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 group-hover:text-red-600 transition-colors duration-300 -ml-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="3" fill="none"/>
              </svg>
            </div>
          </button>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex justify-center items-center min-h-96">
              <div className="text-white text-xl">Đang tải video...</div>
            </div>
          ) : videos.length === 0 ? (
            <div className="flex justify-center items-center min-h-96">
              <div className="text-white text-xl">Chưa có video nào</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 justify-items-center">
              {videos.map((video) => (
                <div key={video.id || video._id} className="relative group cursor-pointer" onClick={() => handleVideoClick(video.id || video._id || 0)}>
                  <div className="relative overflow-hidden rounded-2xl shadow-2xl" style={{ width: '290px', height: '520px' }}>
                    {video.imageUrl ? (
                      <img 
                        src={video.imageUrl}
                        alt={video.title}
                        className="w-full h-full object-cover transition-transform group-hover:scale-110 duration-300"
                        onError={(e) => {
                          // Ẩn ảnh nếu lỗi, không bắn request nữa
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                        <span className="text-gray-400 text-sm">No Image</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    
                    {/* Play Button */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
                        <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Social Media Icons */}
      <SocialMediaIcons />

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl max-w-md w-full mx-4 overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-gray-800 to-gray-700 p-6 text-center">
              {/* Question Mark Icon */}
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl font-bold text-gray-800">?</span>
              </div>
              
              {/* Title */}
              <h2 className="text-2xl font-bold text-white mb-2">Xác nhận</h2>
            </div>

            {/* Modal Content */}
            <div className="p-6 text-center">
              <p className="text-gray-300 text-lg mb-6">
                Bạn có muốn xem video này không?
              </p>
              
              {/* Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={handleConfirm}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors"
                >
                  Có
                </button>
                <button
                  onClick={handleCancel}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors"
                >
                  Không
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logout Modal */}
      <ModalConfirmLogout 
        isShowLogout={showLogoutModal}
        setIsShowLogout={() => setShowLogoutModal(false)}
      />
    </div>
  );
};

export default TongHopVideo;
